import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, ArrowRight, ArrowLeft, CalendarCheck, MessageCircle, AlertTriangle } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { Container } from '@/components/ui/Container';
import { type Treatment } from '@/data/treatments';
import { useTreatments } from '@/lib/useTreatments';
import { formatPula } from '@/lib/money';
import { waLink } from '@/data/site';
import { useBusiness } from '@/lib/business';
import { submitBooking } from '@/lib/firebase';
import { useReveal } from '@/lib/useReveal';
import { PageHeader } from '@/components/ui/PageHeader';
import { closingWarning } from '@/lib/hours';

type Step = 1 | 2 | 3;

export function Booking() {
  useReveal();
  const SITE = useBusiness();
  const { categories, all } = useTreatments();
  const [params] = useSearchParams();
  const [step, setStep] = useState<Step>(1);
  const [selected, setSelected] = useState<Record<string, Treatment>>({});
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [requests, setRequests] = useState('');
  const [hp, setHp] = useState(''); // honeypot
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Pre-select a treatment passed via ?add=<id> (from the Treatments page).
  const addId = params.get('add');
  const applied = useRef(false);
  useEffect(() => {
    if (applied.current || !addId) return;
    const t = all.find((x) => x.id === addId);
    if (t) { setSelected((p) => ({ ...p, [t.id]: t })); applied.current = true; }
  }, [addId, all]);

  const chosen = useMemo(() => Object.values(selected), [selected]);
  const totalPrice = chosen.reduce((s, t) => s + t.price, 0);
  const totalMin = chosen.reduce((s, t) => s + (t.durationMin ?? 0), 0);
  const timeWarning = useMemo(() => closingWarning(SITE.hours, date, time, totalMin), [SITE.hours, date, time, totalMin]);
  const [warnOpen, setWarnOpen] = useState(false);
  const [ackWarning, setAckWarning] = useState('');
  useEffect(() => {
    if (timeWarning && timeWarning !== ackWarning) setWarnOpen(true);
    if (!timeWarning) setWarnOpen(false);
  }, [timeWarning, ackWarning]);

  function toggle(t: Treatment) {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[t.id]) delete next[t.id];
      else next[t.id] = t;
      return next;
    });
  }

  const summaryText =
    `Booking request — Grace Haven Spa\n` +
    chosen.map((t) => `• ${t.name} (${formatPula(t.price)})`).join('\n') +
    `\nTotal: ${formatPula(totalPrice)}${totalMin ? ` · ~${totalMin} min` : ''}` +
    (date ? `\nPreferred: ${date} ${time}` : '') +
    (fullName ? `\nName: ${fullName}` : '');

  async function submit() {
    setError('');
    if (chosen.length === 0) {
      setStep(1);
      setError('Please choose at least one treatment.');
      return;
    }
    if (!fullName.trim() || !email.trim()) {
      setStep(3);
      setError('Please enter your name and email.');
      return;
    }
    if (!phone.trim()) {
      setStep(3);
      setError('A phone number is required so we can confirm your booking.');
      return;
    }
    setBusy(true);
    try {
      await submitBooking({
        hp,
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        date,
        time,
        specialRequests: requests.trim(),
        selectedServices: chosen.map((t) => ({
          id: t.id,
          name: t.name,
          durationMin: t.durationMin ?? 0,
          price: t.price,
        })),
        totalPrice,
        totalMin,
      });
      setDone(true);
    } catch (e) {
      setError(
        e instanceof Error
          ? `We couldn't submit just now (${e.message}). You can send this booking on WhatsApp instead.`
          : 'Something went wrong. Please try WhatsApp.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Seo title="Booking" description="Request a treatment at Grace Haven Spa in Gaborone. Choose your services, pick a time, and we'll confirm by email." />

      <PageHeader eyebrow="Reserve your moment" title="Booking" />

      <section className="bg-cream py-20">
        <Container className="max-w-4xl">
          {done ? (
            <div className="border border-gold/50 bg-paper p-10 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold/20 text-gold-deep">
                <CalendarCheck size={32} />
              </div>
              <h2 className="mt-6 font-display text-3xl text-espresso">Request received</h2>
              <div className="rule-gold mx-auto mt-4" />
              <p className="mx-auto mt-5 max-w-md font-body text-muted">
                Thank you, {fullName.split(' ')[0] || 'and see you soon'}. We've emailed you a copy and
                will confirm your appointment shortly. Keep an eye on your inbox.
              </p>
            </div>
          ) : (
            <div className="grid gap-8 lg:grid-cols-[1fr_300px]">
              {/* Steps */}
              <div className="border border-line bg-paper p-6 sm:p-8">
                <Stepper step={step} />

                {step === 1 && (
                  <div className="mt-8">
                    <h2 className="font-display text-2xl text-espresso">Choose your treatments</h2>
                    <p className="mt-1 font-body text-sm text-muted">Tap to add as many as you like.</p>
                    <div className="mt-6 space-y-7">
                      {categories.map((cat) => (
                        <div key={cat.id}>
                          <p className="eyebrow mb-3">{cat.title}</p>
                          <div className="flex flex-wrap gap-2.5">
                            {cat.treatments.map((t) => {
                              const on = Boolean(selected[t.id]);
                              return (
                                <button
                                  key={t.id}
                                  onClick={() => toggle(t)}
                                  className={`chip ${on ? 'border-gold bg-gold/15 text-espresso' : 'border-line bg-white text-muted hover:border-gold'}`}
                                >
                                  {on && <Check size={14} className="mr-1 inline text-gold-deep" />}
                                  {t.name} · {formatPula(t.price)}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="mt-8">
                    <h2 className="font-display text-2xl text-espresso">When suits you?</h2>
                    <p className="mt-1 font-body text-sm text-muted">A preferred time — we'll confirm availability.</p>
                    <div className="mt-6 grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="label" htmlFor="b-date">Preferred date</label>
                        <input id="b-date" type="date" className="field" value={date} onChange={(e) => setDate(e.target.value)} />
                      </div>
                      <div>
                        <label className="label" htmlFor="b-time">Preferred time</label>
                        <input id="b-time" type="time" className="field" value={time} onChange={(e) => setTime(e.target.value)} />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="label" htmlFor="b-req">Anything we should know?</label>
                      <textarea id="b-req" rows={3} className="field" value={requests} onChange={(e) => setRequests(e.target.value)} placeholder="Pressure preference, allergies, occasion…" />
                    </div>
                    {timeWarning && (
                      <button type="button" onClick={() => setWarnOpen(true)} className="mt-4 flex w-full items-center gap-2 rounded-md border border-blush-deep/40 bg-blush-soft/40 p-3 text-left">
                        <AlertTriangle size={15} className="shrink-0 text-blush-deep" />
                        <span className="font-body text-xs text-espresso">This time runs past closing — tap to see details.</span>
                      </button>
                    )}
                  </div>
                )}

                {step === 3 && (
                  <div className="mt-8">
                    <h2 className="font-display text-2xl text-espresso">Your details</h2>
                    <p className="mt-1 font-body text-sm text-muted">So we can confirm your booking.</p>
                    <div className="mt-6 space-y-4">
                      <div>
                        <label className="label" htmlFor="b-name">Full name</label>
                        <input id="b-name" className="field" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Moeng" />
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label className="label" htmlFor="b-email">Email</label>
                          <input id="b-email" type="email" className="field" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@email.com" />
                        </div>
                        <div>
                          <label className="label" htmlFor="b-phone">Phone <span className="text-blush-deep">*</span></label>
                          <input id="b-phone" className="field" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+267 …" />
                        </div>
                      </div>
                      {/* Honeypot — hidden from real users */}
                      <input
                        type="text"
                        value={hp}
                        onChange={(e) => setHp(e.target.value)}
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden
                        className="absolute left-[-9999px] h-0 w-0 opacity-0"
                      />
                    </div>
                  </div>
                )}

                {error && (
                  <div className="mt-6 rounded-md border border-blush-deep/40 bg-blush-soft/40 p-4">
                    <p className="font-body text-sm text-espresso">{error}</p>
                    {chosen.length > 0 && (
                      <a href={waLink(SITE.whatsapp, summaryText)} target="_blank" rel="noreferrer" className="btn-gold mt-3 !py-2.5 !text-[11px]">
                        <MessageCircle size={14} /> Send on WhatsApp
                      </a>
                    )}
                  </div>
                )}

                {/* Nav buttons */}
                <div className="mt-8 flex items-center justify-between">
                  {step > 1 ? (
                    <button onClick={() => setStep((s) => (s - 1) as Step)} className="btn-outline !px-6">
                      <ArrowLeft size={16} /> Back
                    </button>
                  ) : <span />}
                  {step < 3 ? (
                    <button onClick={() => setStep((s) => (s + 1) as Step)} className="btn-primary !px-7" disabled={step === 1 && chosen.length === 0}>
                      Continue <ArrowRight size={16} />
                    </button>
                  ) : (
                    <button onClick={submit} className="btn-primary !px-7" disabled={busy}>
                      {busy ? 'Sending…' : 'Request booking'} <ArrowRight size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Summary */}
              <aside className="h-fit border border-line bg-espresso-deep p-6 text-cream lg:sticky lg:top-24">
                <p className="eyebrow text-gold-soft">Your selection</p>
                <div className="rule-gold mt-3" />
                {chosen.length === 0 ? (
                  <p className="mt-5 font-body text-sm text-cream/60">No treatments chosen yet.</p>
                ) : (
                  <ul className="mt-5 space-y-3">
                    {chosen.map((t) => (
                      <li key={t.id} className="flex justify-between gap-3 font-body text-sm">
                        <span className="text-cream/85">{t.name}</span>
                        <span className="text-gold-soft">{formatPula(t.price)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-6 border-t border-cream/15 pt-4">
                  <div className="flex justify-between font-body text-sm text-cream/70">
                    <span>Duration</span><span>{totalMin ? `~${totalMin} min` : '—'}</span>
                  </div>
                  <div className="mt-2 flex items-baseline justify-between">
                    <span className="font-body text-sm uppercase tracking-wide text-cream/70">Total</span>
                    <span className="font-display text-2xl text-gold">{formatPula(totalPrice)}</span>
                  </div>
                </div>
                <p className="mt-4 font-body text-[11px] leading-relaxed text-cream/50">
                  Prices in Botswana Pula. Final amount confirmed at the spa, {SITE.address}.
                </p>
              </aside>
            </div>
          )}
        </Container>
      </section>

      {warnOpen && timeWarning && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-espresso-deep/85 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md border border-line bg-cream p-8 text-center shadow-soft">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blush-soft text-blush-deep">
              <AlertTriangle size={28} />
            </div>
            <h2 className="mt-5 font-display text-2xl text-espresso">Just so you know</h2>
            <div className="rule-gold mx-auto mt-3" />
            <p className="mt-5 font-body text-sm leading-relaxed text-muted">{timeWarning}</p>
            <p className="mt-3 font-body text-xs text-muted">You can still send this request and we'll do our best, or pick a time within our hours.</p>
            <div className="mt-7 flex flex-col gap-2.5 sm:flex-row">
              <button onClick={() => { setAckWarning(timeWarning); setWarnOpen(false); setStep(2); }} className="btn-primary flex-1 justify-center">Change my time</button>
              <button onClick={() => { setAckWarning(timeWarning); setWarnOpen(false); }} className="btn-outline flex-1 justify-center">Continue anyway</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Stepper({ step }: { step: Step }) {
  const items = ['Treatments', 'Time', 'Details'];
  return (
    <div className="flex items-center gap-2">
      {items.map((label, i) => {
        const n = (i + 1) as Step;
        const active = step === n;
        const passed = step > n;
        return (
          <div key={label} className="flex flex-1 items-center gap-2">
            <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-body text-xs font-semibold ${
              active ? 'bg-gold text-espresso-deep' : passed ? 'bg-espresso text-cream' : 'bg-line text-muted'
            }`}>
              {passed ? <Check size={14} /> : n}
            </div>
            <span className={`font-body text-xs font-medium ${active || passed ? 'text-espresso' : 'text-muted'}`}>{label}</span>
            {i < items.length - 1 && <div className={`mx-1 h-px flex-1 ${passed ? 'bg-espresso' : 'bg-line'}`} />}
          </div>
        );
      })}
    </div>
  );
}
